/**
 * The tab strip container: pinned order and badges (useProjectTabs, one count source), the `+`
 * New project dialog, and ⌘K. Rendered once by the tab shell above every page.
 */
import { useNavigate } from '@tanstack/react-router'
import { useMemo, useState, useSyncExternalStore } from 'react'
import { useBackend } from '../../lib/backend.js'
import { loadWidth } from '../../lib/explorer.js'
import { projectHref } from '../../lib/ia.js'
import { isStandalone } from '../../lib/shortcut.js'
import { useAppUi } from '../app-ui.js'
import { CallButton } from '../call/CallButton.js' // viko-call
import { useNeedsYou } from '../inbox/useNeedsYou.js'
import { LockedTabChip, useLocked } from '../lock/ContentLock.js'
import { NewProjectDialog } from '../NewProjectDialog.js'
import { ProjectTabs } from '../tabs/ProjectTabs.js'
import { useProjectTabs } from '../tabs/useProjectTabs.js'
import { Explorer } from './Explorer.js'
import { PhoneTop } from './PhoneTop.js'
import { TopBar } from './TopBar.js'
import { useCurrentPlace } from './useRouteProject.js'

/** Phones: the tab strip becomes the place switcher (PhoneTop), so no chip is ever cut off. */
export const PHONE_QUERY = '(max-width: 719px)'
export function usePhone(): boolean {
  return useSyncExternalStore(
    (cb) => {
      const m = matchMedia(PHONE_QUERY)
      m.addEventListener('change', cb)
      return () => m.removeEventListener('change', cb)
    },
    () => matchMedia(PHONE_QUERY).matches,
    () => false,
  )
}

/** The Explorer replaces the tab strip from this width (app-clarity IDE sidebar). */
export const IDE_QUERY = '(min-width: 1280px)'
export function useIde(): boolean {
  return useSyncExternalStore(
    (cb) => {
      const m = matchMedia(IDE_QUERY)
      m.addEventListener('change', cb)
      return () => m.removeEventListener('change', cb)
    },
    () => matchMedia(IDE_QUERY).matches,
    () => false,
  )
}

export function ShellTabs({
  ide = false,
  sidebarHidden = false,
  onSidebarHidden,
}: {
  ide?: boolean
  sidebarHidden?: boolean
  onSidebarHidden?: (hidden: boolean) => void
} = {}) {
  const tabs = useProjectTabs()
  const phone = usePhone()
  const needs = useNeedsYou()
  const [width, setWidth] = useState(loadWidth)
  const ui = useAppUi()
  const b = useBackend()
  const nav = useNavigate()
  const current = useCurrentPlace()
  const [adding, setAdding] = useState(false)
  // Locked: encrypted counts are unknown here; the chip says so instead of badges reading 0.
  const locked = useLocked()
  const standalone = useMemo(() => isStandalone(), [])
  return (
    <>
      {ide ? (
        <>
          {sidebarHidden ? null : (
            <Explorer
              tabs={tabs.tabs}
              unpinned={tabs.unpinned}
              current={current}
              standalone={standalone}
              totals={locked ? { needs: 0, running: 0 } : tabs.totals}
              width={width}
              onWidth={setWidth}
              onMove={tabs.move}
              onUnpin={tabs.unpin}
              onPin={tabs.pin}
              onArchive={(id) => void tabs.archive(id)}
              onAdd={() => setAdding(true)}
              onHide={() => onSidebarHidden?.(true)}
            />
          )}
          <TopBar
            current={current}
            onSearch={() => ui.openPalette()}
            sidebarHidden={sidebarHidden}
            onToggleSidebar={() => onSidebarHidden?.(!sidebarHidden)}
            lockChip={<LockedTabChip />}
            callSlot={<CallButton />}
          />
        </>
      ) : phone ? (
        <PhoneTop
          tabs={tabs.tabs}
          current={current}
          totals={
            locked ? { blocked: 0, needs: 0 } : { blocked: needs.blocked, needs: needs.decide }
          }
          counts={(id) =>
            locked
              ? { blocked: 0, needs: 0 }
              : { blocked: needs.blockedOf(id), needs: needs.decideOf(id) }
          }
          onSearch={() => ui.openPalette()}
          onAdd={() => setAdding(true)}
          lockChip={<LockedTabChip />}
          callSlot={<CallButton />}
        />
      ) : (
        <ProjectTabs
          tabs={tabs.tabs}
          unpinned={tabs.unpinned}
          current={current}
          standalone={standalone}
          totals={locked ? { needs: 0, running: 0 } : tabs.totals}
          lockChip={<LockedTabChip />}
          callSlot={<CallButton />}
          onMove={tabs.move}
          onUnpin={tabs.unpin}
          onPin={tabs.pin}
          onArchive={(id) => void tabs.archive(id)}
          onAdd={() => setAdding(true)}
          onSearch={() => ui.openPalette()}
        />
      )}
      {/* "+": the account-projects New project flow (name + goal, repo, machines, one passkey). */}
      {adding ? (
        <NewProjectDialog
          key={b.artifactAccess.accountId}
          onClose={() => setAdding(false)}
          onCreated={(id) => {
            tabs.pin(id)
            void nav({ href: projectHref(id) })
          }}
        />
      ) : null}
    </>
  )
}
